import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Badge, Col, Row } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';

import { AccountAddress } from '../../hooks/queries/useAccountAddressesQuery';
import { Facility } from '../../hooks/queries/useFacilitiesQuery';
import { TaskRecord, TaskStatus, TaskType } from '../../types';

const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.Completed:
      return 'success';
    case TaskStatus.Active:
      return 'primary';
    case TaskStatus.Planned:
      return 'neutral';
    case TaskStatus.Failed:
      return 'failure';
    case TaskStatus.Cancelled:
      return 'failure';
    default:
      return 'neutral';
  }
};

const getTypeIcon = (type: TaskType) => {
  switch (type) {
    case TaskType.Pickup:
      return '\u2191';
    case TaskType.Delivery:
      return '\u2193';
    case TaskType.Fill:
      return '\u2795';
    case TaskType.Empty:
      return '\u2796';
    case TaskType.Reposition:
      return '\u2194';
    default:
      return '\u2022';
  }
};

const formatLocation = (address?: AccountAddress) => {
  if (!address) return undefined;
  const addressParts = [address.streetAddress, address.city, address.state]
    .filter(Boolean)
    .join(', ');
  const name = address.account?.name;
  if (name && addressParts) return `${name} | ${addressParts}`;
  return name || addressParts || undefined;
};

const formatFacilityLocation = (facility?: Facility) => {
  if (!facility) return undefined;
  const addressParts = facility.address
    ? [
        facility.address.streetAddress,
        facility.address.city,
        facility.address.state,
      ]
        .filter(Boolean)
        .join(', ')
    : undefined;
  if (facility.facilityName && addressParts)
    return `${facility.facilityName} | ${addressParts}`;
  return facility.facilityName || addressParts || undefined;
};

export type ServiceAssetInfo = {
  assetTypeName: string;
  assetGroup?: number;
};

export interface RouteTaskCardProps {
  task: TaskRecord;
  addressMap: Map<string, AccountAddress>;
  facilityMap: Map<string, Facility>;
  serviceAssetMap: Map<string, ServiceAssetInfo[]>;
}

export const RouteTaskCard = ({
  task,
  addressMap,
  facilityMap,
  serviceAssetMap,
}: RouteTaskCardProps) => {
  const t = useTranslations();
  const address = task.accountAddressId
    ? addressMap.get(task.accountAddressId)
    : undefined;
  const facility = task.facilityId
    ? facilityMap.get(task.facilityId)
    : undefined;
  const location = facility
    ? formatFacilityLocation(facility)
    : formatLocation(address);

  const serviceAssets = serviceAssetMap.get(task.fulfillmentServiceId) ?? [];
  const taskGroup = task.assetGroup ?? 1;
  const matchingAsset =
    serviceAssets.find((a) => (a.assetGroup ?? 1) === taskGroup) ??
    (serviceAssets.length === 1 ? serviceAssets[0] : undefined);

  return (
    <div
      className={cn(
        '@RouteTaskCard',
        cn('@TaskCard w-full rounded-md border border-neutral/20 bg-white p-3'),
      )}
    >
      <Row className="items-center justify-between">
        <Row className="items-center gap-2">
          <span className="text-lg">{getTypeIcon(task.type)}</span>
          <span className="font-medium">{task.type}</span>
        </Row>
        <Badge color={getStatusColor(task.status)} size="small">
          {task.status}
        </Badge>
      </Row>
      {(location || matchingAsset || task.routePosition !== undefined) && (
        <Col className="mt-2 gap-1 text-xs text-neutral/60">
          {location && (
            <Row className="items-center gap-1">
              <span>{t.servicesView.form.fields.location}:</span>
              <span>{location}</span>
            </Row>
          )}
          {matchingAsset && (
            <Row className="items-center gap-1">
              <span>{t.servicesView.form.fields.assets.assetType}:</span>
              <span>
                {matchingAsset.assetTypeName}
                {matchingAsset.assetGroup != null &&
                  serviceAssets.length > 1 && (
                    <span className="ml-1 text-neutral/40">
                      (G{matchingAsset.assetGroup})
                    </span>
                  )}
              </span>
            </Row>
          )}
          {task.routePosition !== undefined && (
            <Row className="items-center gap-1">
              <span>
                {t.routesView.form.tabs.routeMap}:{' '}
                {t.routesView.form.services.position} {task.routePosition}
              </span>
            </Row>
          )}
        </Col>
      )}
    </div>
  );
};
